import React, {FC, useMemo} from 'react';
import {useAppSelector} from "../../../../../store/store";
import {OrderCharterCard} from "./charter/OrderCharterCard";
import {OrderEmptyLegCard} from "./emptyLeg/OrderEmptyLegCard";
import styled from "styled-components";
import {CustomResult} from "../../../../../components/UI/AntCustom/CustomResult";
import {useIntl} from "react-intl";
import {CommonFormMessages} from "../../../../../intl/commonMessages/CommonFormMessages";
import {OrderTypeEnum} from "../../../../../store/reducers/chartererRequestsReducer/chartererRequestsReducerTypes";
import {useSearchParams} from "react-router-dom";
import isNumeric from "antd/es/_util/isNumeric";
import {OrderCharterCardSkeleton} from "../../../../../components/UI/SkeletonLoaders/OrderCharterCardSkeleton";
import {EmptyLegCardSkeleton} from "../../../../../components/UI/SkeletonLoaders/EmptyLegCardSkeleton";

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

interface IChartererOrderList {
  isFavorite?: boolean;
  expectedCount?: number;
}

export const ChartererOrderList: FC<IChartererOrderList> = ({isFavorite = false, expectedCount}) => {
  const intl = useIntl();
  const {
    items: allItems,
    isLoading,
  } = useAppSelector(state => state.chartererRequests.itemsObject)

  // Фильтрация по isFavorite теперь происходит на бэкенде, поэтому просто используем все элементы
  // Но оставляем фильтрацию как fallback на случай, если бэкенд вернет не отфильтрованные данные
  const items = useMemo(() => {
    // Если isFavorite=true, но бэкенд вернул не отфильтрованные данные, фильтруем на клиенте
    if (isFavorite) {
      const filtered = allItems.filter(item => item.isFavorite);
      // Если после фильтрации есть элементы, значит бэкенд не отфильтровал - используем отфильтрованные
      // Если элементов нет, но isLoading=false, значит бэкенд правильно отфильтровал - используем все
      return filtered.length > 0 || isLoading ? filtered : allItems;
    }
    return allItems;
  }, [allItems, isFavorite, isLoading]);

  // Calculate skeleton count: use expectedCount if available, otherwise 1
  const skeletonCount = expectedCount !== undefined && expectedCount > 0 ? expectedCount : 1;

  // Show skeleton loaders while loading
  if (isLoading && items.length === 0) {
    return (
      <ListWrapper>
        {[...Array(skeletonCount)].map((_, index) => (
          <OrderCharterCardSkeleton key={`skeleton-${index}`} />
        ))}
      </ListWrapper>
    );
  }

  return (
    <ListWrapper>
      {items.map(order => {
        if ([OrderTypeEnum.ONE_WAY, OrderTypeEnum.ROUND_TRIP, OrderTypeEnum.MULTI_LEG].includes(order.type))
          return <OrderCharterCard key={order.id} order={order} />
        else if (order.type === OrderTypeEnum.EMPTY_LEG)
          return <OrderEmptyLegCard key={order.id} order={order} />
        else
          return null;
      })}

      {items.length === 0 && !isLoading && <CustomResult title={intl.formatMessage(CommonFormMessages.noData)}/>}
    </ListWrapper>
  );
};
